import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { User } from "@/data/mock";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3333/api/v1";
const STORAGE_KEY = "nf_app_token";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  loginWithToken: (token: string) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchMe(token: string): Promise<User> {
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  if (!res.ok) throw new Error("Token inválido");
  const json = await res.json();
  return json.data.user as User;
}

async function tryRefresh(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return null;
    const json = await res.json();
    return (json.data?.token as string) ?? null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem(STORAGE_KEY);

      if (token) {
        try {
          const userData = await fetchMe(token);
          setUser(userData);
        } catch {
          // Access token expired or invalid — try refresh cookie
          const newToken = await tryRefresh();
          if (newToken) {
            localStorage.setItem(STORAGE_KEY, newToken);
            try {
              const userData = await fetchMe(newToken);
              setUser(userData);
            } catch {
              localStorage.removeItem(STORAGE_KEY);
            }
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } else {
        // No access token — try refresh cookie silently
        const newToken = await tryRefresh();
        if (newToken) {
          localStorage.setItem(STORAGE_KEY, newToken);
          try {
            const userData = await fetchMe(newToken);
            setUser(userData);
          } catch {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      }

      setLoading(false);
    };

    init();
  }, []);

  const loginWithToken: AuthContextValue["loginWithToken"] = async (token) => {
    localStorage.setItem(STORAGE_KEY, token);
    const userData = await fetchMe(token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    fetch(`${API_BASE}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
  };

  const refreshUser = async () => {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) return;
    const userData = await fetchMe(token);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithToken, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
